import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
  signal,
  DestroyRef,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideX } from '@ng-icons/lucide';
import { HlmButton, HlmInput, HlmLabel, HlmSpinnerImports } from '@pagai-fe/ui';
import { PasswordInputComponent } from '../../../shared/components/password-input/password-input.component';
import { forgotPasswordErrorMap } from '../../constants/authenticate.constants';
import { AuthenticateStateEnum, ForgotPasswordEnum } from '../../enums/authenticate.enum';
import { CognitoForgotPasswordResult } from '../../interfaces/auth.interfaces';
import { MaskEmailPipe } from '../../pipes/mask-email.pipe';
import { AuthService } from '../../services/auth.service';
import {
  passwordStrengthValidator,
  passwordMatchValidator,
} from '../../validators/password.validators';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    CommonModule,
    HlmButton,
    HlmInput,
    FormsModule,
    ReactiveFormsModule,
    HlmLabel,
    MaskEmailPipe,
    HlmSpinnerImports,
    PasswordInputComponent,
    NgIcon,
  ],
  providers: [provideIcons({ lucideCheck, lucideX })],
  templateUrl: './forgot-password.component.html',
  host: {
    class: 'block',
  },
})
export class ForgotPasswordComponent {
  private readonly _authService: AuthService = inject(AuthService);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly forgotPasswordEnum = ForgotPasswordEnum;
  protected readonly isLoading = signal(false);
  protected readonly forgotPasswordState = signal<ForgotPasswordEnum | undefined>(undefined);
  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly hasError = computed(() => Boolean(this.errorMessage()));

  @Output()
  protected readonly forgotPasswordEvent = new EventEmitter<AuthenticateStateEnum>();

  protected readonly emailForm = new FormGroup({
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
  });

  protected readonly forgotPasswordForm = new FormGroup(
    {
      code: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      password: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, passwordStrengthValidator],
      }),
      confirmPassword: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    },
    { validators: passwordMatchValidator },
  );

  /** Password validation state for UI feedback */
  protected get passwordValidation(): {
    minLength: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    specialChar: boolean;
  } {
    const password = this.forgotPasswordForm.get('password')?.value ?? '';
    return {
      minLength: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    };
  }

  /** Check if passwords match */
  protected get passwordsMatch(): boolean {
    const password = this.forgotPasswordForm.get('password')?.value ?? '';
    const confirmPassword = this.forgotPasswordForm.get('confirmPassword')?.value ?? '';
    return password.length > 0 && password === confirmPassword;
  }

  protected sendCodeResetPassword(): void {
    if (this.emailForm.valid) {
      this.isLoading.set(true);
      const { email } = this.emailForm.getRawValue();
      this._authService
        .forgotPassword(email)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (result: CognitoForgotPasswordResult) => {
            this.forgotPasswordState.set(result.type);
          },
          error: (error: CognitoForgotPasswordResult) => {
            this.isLoading.set(false);
            this.forgotPasswordState.set(ForgotPasswordEnum.FAIL);
            this.errorMessage.set(forgotPasswordErrorMap.get(error.result?.name));
          },
          complete: () => {
            this.isLoading.set(false);
          },
        });
    }
  }

  protected confirmNewPassword(): void {
    if (
      this.forgotPasswordForm.valid &&
      !this.forgotPasswordForm.hasError('passwordMismatch')
    ) {
      this.isLoading.set(true);
      const { email } = this.emailForm.getRawValue();
      const { code, password } = this.forgotPasswordForm.getRawValue();
      this._authService
        .confirmNewPassword(email, code, password)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: () => {
            this.isLoading.set(false);
            this.forgotPasswordState.set(ForgotPasswordEnum.SUCCESS);
          },
          error: () => this.isLoading.set(false),
        });
    }
  }

  protected onSignInClick(): void {
    this.forgotPasswordEvent.emit(AuthenticateStateEnum.SIGN_IN);
  }

  protected onBackToEmail(): void {
    this.forgotPasswordState.set(undefined);
  }
}
